import { useEffect } from "react";
import { promoDiscount, useActivePromotion } from "@/hooks/useActivePromotion";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useNavigate } from "react-router-dom";
import { T } from "@/context/TextEditContext";

export default function CartDrawer({ open, onClose }) {
  const { items, setQty, remove, subtotal, count } = useCart();
  const promo = useActivePromotion("online");
  const promoOff = promoDiscount(subtotal, promo);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleCheckout = () => {
    onClose();
    navigate("/checkout");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer">
      <div
        className="absolute inset-0 bg-stone-900/60"
        onClick={onClose}
        aria-label="Close cart"
      />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-screen max-w-md bg-[#FDFBF7] rounded-t-3xl shadow-2xl animate-drawer-in overflow-x-hidden">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-stone-200">
          <div>
            <div className="font-display text-2xl text-brand-red">
              <T k="cart.title" d="Your Cart" />
            </div>
            <div className="text-xs text-stone-500 uppercase tracking-widest">
              {count} {count === 1 ? "item" : "items"}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="cart-close-btn"
            className="p-2 rounded-full hover:bg-stone-100"
          >
            <X className="w-5 h-5 text-stone-700" />
          </button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto px-5 py-3">
          {items.length === 0 ? (
            <div className="text-center py-10 text-stone-500" data-testid="cart-empty-state">
              <div className="font-hand text-3xl text-stone-400">
                <T k="cart.empty_title" d="Nothing here yet" />
              </div>
              <div className="text-sm mt-1">
                <T k="cart.empty_sub" d="Add something delicious!" />
              </div>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((i) => {
                // Support both v1 (item_id-keyed) and v2 (line_id-keyed) cart entries.
                const key = i.line_id || i.item_id;
                return (
                <li
                  key={key}
                  data-testid={`cart-line-${key}`}
                  className="flex items-start gap-3 bg-white border border-stone-200 rounded-xl p-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-stone-800 truncate">{i.name}</div>
                    {i.modifications && (
                      <div className="text-[11px] text-stone-600 italic leading-tight mt-0.5">
                        {i.modifications}
                      </div>
                    )}
                    <div className="text-brand-red font-bold text-sm mt-0.5">
                      ${(i.price * i.quantity).toFixed(2)}
                    </div>
                    <div className="text-[11px] text-stone-500">
                      ${i.price.toFixed(2)} each
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2 bg-stone-100 rounded-full px-1 py-1">
                      <button
                        onClick={() => setQty(key, i.quantity - 1)}
                        className="w-6 h-6 rounded-full bg-white border border-stone-300 flex items-center justify-center hover:bg-stone-50"
                        data-testid={`cart-qty-dec-${key}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span
                        className="text-sm font-bold w-5 text-center"
                        data-testid={`cart-item-quantity-${key}`}
                      >
                        {i.quantity}
                      </span>
                      <button
                        onClick={() => setQty(key, i.quantity + 1)}
                        className="w-6 h-6 rounded-full bg-brand-red text-white flex items-center justify-center hover:bg-red-800"
                        data-testid={`cart-qty-inc-${key}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <button
                      onClick={() => remove(key)}
                      className="text-stone-400 hover:text-brand-red text-xs flex items-center gap-1"
                      data-testid={`cart-remove-${key}`}
                    >
                      <Trash2 className="w-3 h-3" />
                      <T k="cart.remove" d="Remove" />
                    </button>
                  </div>
                </li>
              );})}
            </ul>
          )}
        </div>

        <div className="px-5 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-stone-200 space-y-3 bg-white rounded-t-2xl">
          <div className="flex items-center justify-between">
            <span className="text-sm uppercase tracking-widest text-stone-500">
              <T k="common.subtotal" d="Subtotal" />
            </span>
            <span
              data-testid="cart-subtotal"
              className="text-2xl font-display text-brand-red"
            >
              ${subtotal.toFixed(2)}
            </span>
          </div>
          {promoOff > 0 && (
            <div className="flex items-center justify-between -mt-2" data-testid="cart-promo-line">
              <span className="text-xs uppercase tracking-widest text-emerald-700 font-bold">{promo.name} · {Number(promo.percent) % 1 === 0 ? promo.percent : Number(promo.percent).toFixed(1)}% off</span>
              <span className="text-emerald-700 font-black" data-testid="cart-promo-discount">−${promoOff.toFixed(2)}</span>
            </div>
          )}
          <button
            onClick={handleCheckout}
            disabled={items.length === 0}
            data-testid="cart-checkout-btn"
            className="w-full bg-brand-red text-white py-3.5 rounded-full font-bold uppercase tracking-widest text-sm hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
          >
            <T k="cart.checkout_btn" d="Checkout · Pay by Card" />
          </button>
          <div className="text-center text-[11px] text-stone-400">
            <T k="cart.secure_note" d="Card is charged securely via Square" />
          </div>
        </div>
      </div>
    </div>
  );
}
